import { Link } from 'react-router-dom';
import {
  Kaart,
  KaartKop,
  Knop,
  KnopLink,
  Leeg,
  PaginaKop,
  SkeletRegels,
  SkeletTegels,
  Tegel,
  WedstrijdRegel,
  useToast
} from '../components/ui';
import { IcoonPlus } from '../components/icons';
import { LijnGrafiek } from '../components/charts';
import { OpenstaandeUitslagen } from '../components/Akkoord';
import { SERIES } from '../lib/colors';
import { rangBanden } from '../lib/prestaties';
import { useAuth } from '../lib/auth';
import { START_RATING, chronologisch, kantVan, potjesVan, ranglijst as maakRanglijst, reeksVan, relatiesVan, spelerStats } from '../lib/elo';
import { fmtDatum, relatief, toonNaam, toonRating } from '../lib/format';
import { useBeantwoordUitnodiging, useLeague, usePouleLeden, usePoules, useUitnodigingen } from '../lib/queries';
import { usePoule } from '../lib/poule';

/**
 * Home is van jou, niet van de competitie: je eigen cijfers, je eigen poules en
 * wat er op je ligt te wachten. De brede overzichten staan op Ranglijst en
 * Historie — daar hoort het thuis, niet op het scherm waar je binnenkomt.
 */
export default function Home() {
  const { pouleId } = usePoule();
  const { profile } = useAuth();
  const { players, matches, elo, zoekSpeler, laden } = useLeague(pouleId);
  const poules = usePoules();
  const leden = usePouleLeden();
  const uitnodigingen = useUitnodigingen();
  const beantwoorden = useBeantwoordUitnodiging();
  const meld = useToast();

  const voornaam = (profile?.name ?? '').split(' ')[0] || 'speler';

  /* Je account ís de speler: geen koppeling meer nodig. */
  const mijnSpeler = profile ? players.find((p) => p.id === profile.id) : undefined;

  const open = (uitnodigingen.data ?? []).filter((u) => u.profielId === profile?.id && u.status === 'open');
  const mijnLidmaatschappen = (leden.data ?? []).filter((l) => l.profielId === profile?.id);
  const mijnPoules = (poules.data ?? []).filter((p) => mijnLidmaatschappen.some((l) => l.pouleId === p.id));

  if (laden) {
    return (
      <>
        <PaginaKop titel={`Hallo, ${voornaam}`} />
        <SkeletTegels />
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="Jouw laatste potjes" />
            <SkeletRegels aantal={5} />
          </Kaart>
        </div>
      </>
    );
  }

  const eigenPotjes = mijnSpeler ? chronologisch(potjesVan(mijnSpeler.id, matches)).reverse() : [];
  const stats = mijnSpeler ? spelerStats(mijnSpeler.id, matches) : null;
  const reeks = mijnSpeler ? reeksVan(mijnSpeler.id, matches) : null;
  const rel = mijnSpeler ? relatiesVan(mijnSpeler.id, matches) : null;
  const stand = maakRanglijst(players.filter((p) => p.isActive), matches, elo.ratings);
  const mijnPlek = mijnSpeler ? stand.findIndex((r) => r.player.id === mijnSpeler.id) + 1 : 0;

  /* Je eigen ratingverloop, met het startpunt ervoor zodat de lijn ergens begint. */
  const verloop = mijnSpeler ? (elo.verloop[mijnSpeler.id] ?? []) : [];
  const mijnVerloop = verloop.length
    ? [
        { x: new Date(verloop[0].at).getTime() - 86400000, y: START_RATING },
        ...verloop.map((v) => ({ x: new Date(v.at).getTime(), y: v.rating }))
      ]
    : [];

  return (
    <>
      <PaginaKop
        titel={`Hallo, ${voornaam}`}
        onder={
          mijnSpeler
            ? 'Jouw stand van zaken aan de tafel'
            : 'Welkom. Hieronder komt jouw stand te staan zodra je meespeelt.'
        }
        rechts={
          <KnopLink to="/nieuw" variant="primair">
            <IcoonPlus /> Uitslag invoeren
          </KnopLink>
        }
      />

      {open.length ? (
        <div className="mb-3.5 border-2 border-krijt-900 bg-signaal/25 p-4">
          <p className="kop mb-3 text-[15px]">
            {open.length === 1 ? 'Je bent uitgenodigd' : `${open.length} uitnodigingen`}
          </p>
          <div className="flex flex-col gap-2">
            {open.map((u) => {
              const poule = (poules.data ?? []).find((p) => p.id === u.pouleId);
              return (
                <div key={u.id} className="flex flex-wrap items-center gap-3 border border-krijt-300 bg-white px-3 py-2.5">
                  <span className="font-semibold">{poule?.naam ?? 'Een poule'}</span>
                  {poule?.prijs ? <span className="text-sm text-krijt-500">speelt om: {poule.prijs}</span> : null}
                  <span className="ml-auto flex gap-2">
                    <Knop
                      klein
                      variant="primair"
                      onClick={async () => {
                        try {
                          await beantwoorden.mutateAsync({ id: u.id, akkoord: true });
                          meld('Je doet mee.');
                        } catch (err) {
                          meld(err instanceof Error ? err.message : 'Mislukt.', true);
                        }
                      }}
                    >
                      Meedoen
                    </Knop>
                    <Knop
                      klein
                      onClick={async () => {
                        try {
                          await beantwoorden.mutateAsync({ id: u.id, akkoord: false });
                          meld('Afgeslagen.');
                        } catch (err) {
                          meld(err instanceof Error ? err.message : 'Mislukt.', true);
                        }
                      }}
                    >
                      Nee, bedankt
                    </Knop>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Wat er op een handtekening wacht staat bovenaan: het telt pas mee als
          beide kanten hebben getekend, dus laat het niet liggen. */}
      <div className="mb-3.5 empty:mb-0">
        <OpenstaandeUitslagen pouleId={pouleId} />
      </div>

      {mijnSpeler && stats && reeks ? (
        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          <Tegel
            label="Jouw rating"
            waarde={toonRating(elo.ratings[mijnSpeler.id] ?? START_RATING)}
            onder={mijnPlek ? `${mijnPlek}e op de ranglijst` : 'nog geen plek'}
          />
          <Tegel
            label="Winstpercentage"
            waarde={`${Math.round(stats.winstPct)}%`}
            onder={`${stats.gewonnen} gewonnen, ${stats.verloren} verloren`}
          />
          <Tegel
            label="Potjes"
            waarde={stats.gespeeld}
            onder={eigenPotjes.length ? `laatste: ${relatief(eigenPotjes[0].playedAt)}` : 'nog niet gespeeld'}
          />
          <Tegel
            label="Reeks"
            waarde={reeks.aantal ? `${reeks.aantal}×` : '—'}
            onder={
              reeks.aantal ? (reeks.soort === 'winst' ? 'gewonnen op rij' : 'verloren op rij') : 'nog niet gespeeld'
            }
          />
        </div>
      ) : (
        <Kaart>
          <KaartKop titel="Nog geen potje gespeeld" />
          <p className="text-sm leading-relaxed text-krijt-600">
            Zodra je eerste uitslag is ingevoerd staat hier je rating, je vorm en je plek op de ranglijst. Je begint op
            800, net als iedereen.
          </p>
        </Kaart>
      )}

      {mijnVerloop.length > 1 ? (
        <div className="mt-3.5">
          <Kaart>
            <KaartKop
              titel="Jouw rating in de tijd"
              hint={
                <Link to={`/speler/${mijnSpeler?.id}`} className="hover:underline">
                  je hele profiel →
                </Link>
              }
            />
            <LijnGrafiek
              reeksen={[{ naam: 'jij', kleur: SERIES[0], punten: mijnVerloop }]}
              hoogte={200}
              basis={START_RATING}
              banden={rangBanden()}
            />
            <p className="mt-2 text-xs text-krijt-500">
              Elk punt is een bevestigd potje. De vlakken erachter zijn de rangen; de stippellijn is waar iedereen
              begint.
            </p>
          </Kaart>
        </div>
      ) : null}

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-[1fr_340px]">
        <Kaart>
          <KaartKop
            titel="Jouw laatste potjes"
            hint={
              <Link to="/historie" className="hover:underline">
                hele historie →
              </Link>
            }
          />
          {eigenPotjes.length ? (
            eigenPotjes
              .slice(0, 6)
              .map((m) => (
                <WedstrijdRegel key={m.id} potje={m} zoekSpeler={zoekSpeler} delta={elo.perPotje[m.id]?.deltaA} deltaB={elo.perPotje[m.id]?.deltaB} />
              ))
          ) : (
            <Leeg tekst="Nog geen potjes van jou. Speel er een en voer de uitslag in." ikoon="bal" />
          )}
        </Kaart>

        <div className="flex flex-col gap-3.5">
          <Kaart>
            <KaartKop
              titel="Jouw poules"
              hint={
                <Link to="/poules" className="hover:underline">
                  alles zien →
                </Link>
              }
            />
            {mijnPoules.length ? (
              <div className="flex flex-col">
                {mijnPoules.map((p) => {
                  const rol = mijnLidmaatschappen.find((l) => l.pouleId === p.id)?.rol;
                  return (
                    <Link
                      key={p.id}
                      to={`/poules/${p.id}`}
                      className="flex items-center gap-2 border-b border-krijt-200 py-2.5 last:border-b-0 hover:bg-krijt-50"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold">{p.naam}</span>
                        {p.prijs ? <span className="text-xs text-krijt-500">om: {p.prijs}</span> : null}
                      </span>
                      {rol === 'beheerder' ? (
                        <span className="opschrift border border-veld-300 bg-veld-50 px-1.5 py-0.5 text-veld-800">
                          beheerder
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            ) : (
              <Leeg tekst="Je zit nog in geen poule." ikoon="poule" />
            )}
          </Kaart>

          {mijnSpeler && rel ? (
            <Kaart>
              <KaartKop titel="Met en tegen" hint="vanaf drie potjes" />
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="opschrift text-krijt-500">Beste ploeggenoot</dt>
                  <dd className="mt-0.5">
                    {rel.ploeggenoot ? (
                      <span className="flex items-center justify-between gap-2">
                        <Link to={`/speler/${rel.ploeggenoot.id}`} className="font-semibold hover:underline">
                          {toonNaam(zoekSpeler(rel.ploeggenoot.id))}
                        </Link>
                        <span className="nums text-krijt-500">{Math.round(rel.ploeggenoot.pct)}% winst</span>
                      </span>
                    ) : (
                      <span className="text-krijt-500">nog te weinig 2v2 gespeeld</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="opschrift text-krijt-500">Lastigste tegenstander</dt>
                  <dd className="mt-0.5">
                    {rel.boeman ? (
                      <span className="flex items-center justify-between gap-2">
                        <Link to={`/speler/${rel.boeman.id}`} className="font-semibold hover:underline">
                          {toonNaam(zoekSpeler(rel.boeman.id))}
                        </Link>
                        <span className="nums text-krijt-500">{Math.round(rel.boeman.pct)}% winst</span>
                      </span>
                    ) : (
                      <span className="text-krijt-500">nog te weinig gespeeld</span>
                    )}
                  </dd>
                </div>
              </dl>
            </Kaart>
          ) : null}

          {mijnSpeler && eigenPotjes.length ? (
            <Kaart>
              <KaartKop titel="Jouw vorm" hint="laatste vijf" />
              <div className="flex items-center gap-1.5">
                {chronologisch(potjesVan(mijnSpeler.id, matches))
                  .slice(-5)
                  .map((m) => (
                    <span
                      key={m.id}
                      title={`${fmtDatum(m.playedAt)}: ${m.scoreA}–${m.scoreB}`}
                      className={`flex h-8 flex-1 items-center justify-center text-xs font-bold text-white ${
                        kantVan(m, mijnSpeler.id).gewonnen ? 'bg-veld-600' : 'bg-kritiek'
                      }`}
                    >
                      {kantVan(m, mijnSpeler.id).gewonnen ? 'W' : 'V'}
                    </span>
                  ))}
              </div>
            </Kaart>
          ) : null}
        </div>
      </div>
    </>
  );
}
